import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { IoCloseSharp } from "react-icons/io5";
import { Button, Modal, ModalBody, Table } from "reactstrap";
import { updateCategoriesFromExcel } from "../../api/dnhCategories.api";
import { uploadCategoryExcelFile } from "../../firebase/utility";
import GeneralButton from "../button/GeneralButton";
import Separator from "../Separator";

const UploadCategoriesExcelModal = ({ isOpen, toggle, allCategories, type = "category" }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [uploadedFilePath, setUploadedFilePath] = useState("");
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setLoading(false);
    setPreviewData([]);
    setIsPreviewMode(false);
    setUploadedFilePath("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleToggle = () => {
    resetState();
    toggle();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUploadAndPreview = async () => {
    if (!file) {
      toast.error("Please select an Excel file first.");
      return;
    }

    setLoading(true);
    try {
      const { url, path } = await uploadCategoryExcelFile(file);
      setUploadedFilePath(path);

      updateCategoriesFromExcel(path, type, true, (res) => {
        if (res.status === 200) {
          setPreviewData(res.data || {});
          setIsPreviewMode(true);
          toast.success("Categories preview loaded successfully.");
        } else {
          toast.error(res.message || "Failed to load preview.");
        }
        setLoading(false);
      });
    } catch (error) {
      toast.error("Error uploading file: " + error.message);
      setLoading(false);
    }
  };

  const handleFinalImport = () => {
    if (!uploadedFilePath) {
      toast.error("Missing file path.");
      return;
    }

    setLoading(true);
    updateCategoriesFromExcel(uploadedFilePath, type, false, (res) => {
      if (res.status === 200) {
        toast.success("Categories updated successfully!");
        handleToggle();
      } else {
        toast.error(res.message || "Failed to update categories.");
      }
      setLoading(false);
    });
  };

  const validRows = previewData?.validRows || [];
  const invalidRows = previewData?.invalidRows || [];
  const notFoundRows = previewData?.notFoundRows || [];

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered size="xl">
      <ModalBody className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4 className="primary-color">Upload {type === 'subcategory' ? 'Sub-Categories' : 'Categories'} from Excel</h4>
          <IoCloseSharp size={"30"} onClick={handleToggle} className="pointer" />
        </div>
        <Separator />

        <div className="mt-4">
          {!isPreviewMode ? (
            <div className="row justify-content-center">
              <div className="col-md-8 mb-3 text-center">
                <label className="form-label fw-bold">Select Excel File</label>
                <div className="mb-2 text-muted small">
                  Expected columns: slug, id, {type === 'subcategory' ? 'subCategoryName, subCategoryImage' : 'categoryName, categoryImage'}
                </div>
                <input
                  type="file"
                  className="form-control"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
              </div>
              <div className="col-12 mt-3 text-center">
                <GeneralButton
                  title={loading ? "Uploading..." : "Upload & Preview"}
                  className="blue-button"
                  width="200px"
                  loading={loading}
                  disabled={loading || !file}
                  handleClick={handleUploadAndPreview}
                />
              </div>
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Preview {type === 'subcategory' ? 'Sub-Categories' : 'Categories'} ({previewData.totalProcessed || 0} total rows processed)</h5>
                <Button color="link" onClick={() => setIsPreviewMode(false)}>
                  Back to Upload
                </Button>
              </div>

              <div className="d-flex gap-4 mb-3">
                <span className="text-success fw-bold">Valid: {previewData.validCount || 0}</span>
                <span className="text-danger fw-bold">Invalid: {previewData.invalidCount || 0}</span>
                <span className="text-warning fw-bold">Not Found: {previewData.notFoundCount || 0}</span>
              </div>

              <div
                style={{
                  maxHeight: "500px",
                  overflow: "auto",
                  border: "1px solid #eee",
                }}
              >
                <Table striped hover responsive style={{ borderCollapse: 'separate' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1, boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>
                    <tr>
                      <th style={{ width: '80px' }}>Row</th>
                      <th>{type === 'subcategory' ? 'Sub-Category Name' : 'Category Name'}</th>
                      <th>Slug</th>
                      <th>{type === 'subcategory' ? 'Sub-Category Image' : 'Category Image'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validRows.length > 0 ? (
                      validRows.map((item, index) => {
                        const rowData = item.updatePayload || {};
                        return (
                          <tr key={index}>
                            <td>{item.rowNumber}</td>
                            <td>{rowData[type === 'subcategory' ? 'subCategoryName' : 'categoryName'] || "N/A"}</td>
                            <td>{rowData.slug || "N/A"}</td>
                            <td className="text-truncate" style={{ maxWidth: '200px' }}>{rowData[type === 'subcategory' ? 'subCategoryImage' : 'categoryImage'] || "N/A"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No valid {type === 'subcategory' ? 'sub-categories' : 'categories'} found in the file.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {invalidRows.length > 0 && (
                <div className="mt-4">
                  <h6 className="text-danger">Invalid Rows</h6>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    <Table size="sm" striped>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invalidRows.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.rowNumber}</td>
                            <td>{row.errors?.join(", ") || row.error || "Unknown error"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              {notFoundRows.length > 0 && (
                <div className="mt-4">
                  <h6 className="text-warning">Not Found Rows</h6>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    <Table size="sm" striped>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Identifier</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notFoundRows.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.rowNumber}</td>
                            <td>{row.identifier || "N/A"}</td>
                            <td className="text-danger">{row.error || "Not found in database"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              <div className="mt-4 text-center">
                <GeneralButton
                  title={loading ? "Updating..." : "Confirm Update"}
                  className="blue-button"
                  width="200px"
                  loading={loading}
                  disabled={loading || validRows.length === 0}
                  handleClick={handleFinalImport}
                />
              </div>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default UploadCategoriesExcelModal;
